import type { BodyMode, HeaderRow, ResponseDraft } from './draft.js';
import { MAX_DELAY_MS } from './types.js';

const INPUT =
  'rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 ' +
  'placeholder:text-slate-600 focus:border-sky-500 focus:outline-none';

const LABEL = 'text-xs font-medium uppercase tracking-wide text-slate-400';

export interface ResponseFieldsProps {
  draft: ResponseDraft;
  onChange: (draft: ResponseDraft) => void;
  /** Prefix for `data-testid`, so two of these can live on one screen. */
  idPrefix: string;
  /** The default response has no delay to configure — only rules do. */
  withDelay?: boolean;
}

/** Status, headers, body and delay. Shared by the rule editor and the default. */
export function ResponseFields({
  draft,
  onChange,
  idPrefix,
  withDelay = true,
}: ResponseFieldsProps) {
  const patch = (fields: Partial<ResponseDraft>): void => onChange({ ...draft, ...fields });

  const setHeader = (index: number, row: Partial<HeaderRow>): void =>
    patch({
      headers: draft.headers.map((header, at) => (at === index ? { ...header, ...row } : header)),
    });

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap gap-4">
        <label className="grid gap-1">
          <span className={LABEL}>Status</span>
          <input
            className={`${INPUT} w-24`}
            data-testid={`${idPrefix}-status`}
            inputMode="numeric"
            value={draft.status}
            onChange={(event) => patch({ status: event.target.value })}
          />
        </label>

        {withDelay ? (
          <label className="grid gap-1">
            <span className={LABEL}>Retardo (ms)</span>
            <input
              className={`${INPUT} w-28`}
              data-testid={`${idPrefix}-delay`}
              inputMode="numeric"
              max={MAX_DELAY_MS}
              value={draft.delayMs}
              onChange={(event) => patch({ delayMs: event.target.value })}
            />
          </label>
        ) : null}
      </div>

      <div className="grid gap-2">
        <span className={LABEL}>Cabeceras</span>

        {draft.headers.map((header, index) => (
          <div key={index} className="flex gap-2">
            <input
              aria-label={`Nombre de la cabecera ${index + 1}`}
              className={`${INPUT} w-48`}
              placeholder="content-type"
              value={header.name}
              onChange={(event) => setHeader(index, { name: event.target.value })}
            />
            <input
              aria-label={`Valor de la cabecera ${index + 1}`}
              className={`${INPUT} flex-1`}
              placeholder="application/json"
              value={header.value}
              onChange={(event) => setHeader(index, { value: event.target.value })}
            />
            <button
              type="button"
              className="rounded-md border border-slate-700 px-2 text-sm text-slate-400 hover:text-rose-400"
              aria-label={`Quitar la cabecera ${index + 1}`}
              onClick={() => patch({ headers: draft.headers.filter((_, at) => at !== index) })}
            >
              ×
            </button>
          </div>
        ))}

        <button
          type="button"
          className="justify-self-start rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-sky-600"
          data-testid={`${idPrefix}-add-header`}
          onClick={() => patch({ headers: [...draft.headers, { name: '', value: '' }] })}
        >
          + cabecera
        </button>
      </div>

      <div className="grid gap-2">
        <div className="flex items-center gap-3">
          <span className={LABEL}>Cuerpo</span>
          <select
            aria-label="Tipo de cuerpo"
            className={`${INPUT} py-0.5`}
            data-testid={`${idPrefix}-body-mode`}
            value={draft.bodyMode}
            onChange={(event) => patch({ bodyMode: event.target.value as BodyMode })}
          >
            <option value="json">JSON</option>
            <option value="text">Texto</option>
            <option value="none">Sin cuerpo</option>
          </select>
        </div>

        {draft.bodyMode === 'none' ? null : (
          <textarea
            className={`${INPUT} min-h-28 font-mono`}
            data-testid={`${idPrefix}-body`}
            spellCheck={false}
            value={draft.bodyText}
            onChange={(event) => patch({ bodyText: event.target.value })}
          />
        )}

        <p className="text-xs text-slate-500">
          Plantillas: <code>{'{{path.id}}'}</code>, <code>{'{{query.foo}}'}</code>,{' '}
          <code>{'{{body.user.email}}'}</code>, <code>{'{{headers.x-request-id}}'}</code>,{' '}
          <code>{'{{uuid}}'}</code>, <code>{'{{now}}'}</code>, <code>{'{{randomInt 1 100}}'}</code>.
          Un valor que no exista se sustituye por cadena vacía.
        </p>
      </div>
    </div>
  );
}
